import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Pencil, Trash, PlusCircle, MagnifyingGlass, X, DownloadSimple } from "@phosphor-icons/react";

export default function ShopProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/products", { params: q ? { q } : {} });
    setProducts(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/products/inventory-report", { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : `inventory-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Inventory report downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download report");
    }
    setDownloading(false);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Products</h1>
          <p className="text-gray-600 text-sm">{products.length} item{products.length !== 1 && "s"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={downloadReport} disabled={downloading}
            className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-300 text-sm font-bold px-3 py-2 rounded-xl transition active:scale-95 disabled:opacity-60"
            data-testid="products-download-report">
            <DownloadSimple weight="bold" size={16} />
            {downloading ? "Preparing…" : "Download Inventory Report"}
          </button>
          <Link to="/admin/products/new" className="qd-btn-primary inline-flex items-center gap-2" data-testid="products-add-btn">
            <PlusCircle weight="bold" size={18} /> Add with AI
          </Link>
        </div>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="qd-input pl-10"
          placeholder="Search products…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="products-search"
        />
      </div>

      {loading ? <div className="text-gray-500">Loading…</div> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {products.map((p) => (
            <div key={p.id} className="qd-card overflow-hidden" data-testid={`product-card-${p.id}`}>
              <div className="aspect-square bg-gray-50 overflow-hidden relative">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.name} className={`w-full h-full object-cover ${p.inventory <= 0 ? "grayscale" : ""}`} />
                ) : <div className="w-full h-full flex items-center justify-center text-gray-300">No image</div>}
                {p.discount_pct > 0 && (
                  <div className="absolute top-2 left-2 bg-[#15803D] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {p.discount_pct}% OFF
                  </div>
                )}
                {!p.is_active && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span className="text-white font-bold text-xs uppercase tracking-widest">Disabled</span>
                  </div>
                )}
                {p.is_active && p.inventory <= 0 && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                    data-testid={`product-oos-${p.id}`}>
                    Out of stock
                  </div>
                )}
                {p.is_active && p.inventory > 0 && p.inventory <= 5 && (
                  <div className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                    Low stock
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-xs text-gray-500">{p.weight}</div>
                <div className="font-semibold text-sm line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-heading font-black">₹{p.price}</span>
                  {p.mrp > p.price && <span className="text-xs text-gray-400 line-through">₹{p.mrp}</span>}
                </div>
                <div className="text-xs text-gray-500 mt-1">Stock: {p.inventory}</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(p)}
                    className="flex-1 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-semibold flex items-center justify-center gap-1 transition active:scale-95"
                    data-testid={`product-edit-${p.id}`}>
                    <Pencil size={14} /> Edit
                  </button>
                  <button onClick={() => del(p.id)}
                    className="py-1.5 px-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs transition active:scale-95"
                    data-testid={`product-delete-${p.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {products.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              No products yet. <Link to="/admin/products/new" className="text-[#15803D] font-semibold underline">Add your first</Link>
            </div>
          )}
        </div>
      )}

      {editing && (
        <EditProductModal
          product={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditProductModal({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product.name || "",
    description: product.description || "",
    price: product.price ?? "",
    mrp: product.mrp ?? "",
    weight: product.weight || "",
    inventory: product.inventory ?? 0,
    category_id: product.category_id || "",
    is_active: product.is_active !== false,
    images: product.images || [],
  });
  const [saving, setSaving] = useState(false);
  const [imgInput, setImgInput] = useState("");

  const update = (k, v) => setForm({ ...form, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || form.price === "" || isNaN(parseFloat(form.price))) {
      return toast.error("Name and price are required");
    }
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}`, {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        mrp: form.mrp === "" ? null : parseFloat(form.mrp),
        weight: form.weight,
        inventory: parseInt(form.inventory) || 0,
        category_id: form.category_id || null,
        is_active: form.is_active,
        images: form.images,
      });
      toast.success("Product updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      update("images", [...form.images, e.target.result]);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 fade-up" onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-product-modal">
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-black text-xl">Edit product</h2>
            <p className="text-xs text-gray-500">Update details and save</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200" data-testid="edit-modal-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={save} className="p-5 space-y-4">
          {form.images.length > 0 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {form.images.map((img, i) => (
                <div key={i} className="relative flex-shrink-0">
                  <img src={img} alt="" className="w-20 h-20 rounded-xl object-cover" />
                  <button type="button"
                    onClick={() => update("images", form.images.filter((_, idx) => idx !== i))}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/70 text-white text-xs flex items-center justify-center"
                    data-testid={`edit-remove-img-${i}`}>
                    <X size={10} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="qd-input flex items-center justify-center gap-2 cursor-pointer text-sm font-semibold !bg-gray-100 hover:!bg-gray-200">
              + Upload image
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])} data-testid="edit-img-upload" />
            </label>
            <div className="flex gap-2">
              <input className="qd-input flex-1" placeholder="Or paste URL"
                value={imgInput} onChange={(e) => setImgInput(e.target.value)} data-testid="edit-img-url" />
              <button type="button"
                onClick={() => { if (imgInput) { update("images", [...form.images, imgInput]); setImgInput(""); } }}
                className="px-3 rounded-xl bg-gray-100 text-sm font-bold hover:bg-gray-200" data-testid="edit-img-add">
                Add
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Name *</label>
            <input className="qd-input mt-2" value={form.name}
              onChange={(e) => update("name", e.target.value)} data-testid="edit-name" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Description</label>
            <textarea rows="2" className="qd-input mt-2 resize-none" value={form.description}
              onChange={(e) => update("description", e.target.value)} data-testid="edit-desc" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Price *</label>
              <input type="number" min="0" step="0.01" className="qd-input mt-2" value={form.price}
                onChange={(e) => update("price", e.target.value)} data-testid="edit-price" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">MRP</label>
              <input type="number" min="0" step="0.01" className="qd-input mt-2" value={form.mrp}
                onChange={(e) => update("mrp", e.target.value)} data-testid="edit-mrp" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Weight</label>
              <input className="qd-input mt-2" value={form.weight}
                onChange={(e) => update("weight", e.target.value)} placeholder="500g" data-testid="edit-weight" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Stock</label>
              <input type="number" min="0" className="qd-input mt-2" value={form.inventory}
                onChange={(e) => update("inventory", e.target.value)} data-testid="edit-inventory" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Category</label>
              <select className="qd-input mt-2" value={form.category_id}
                onChange={(e) => update("category_id", e.target.value)} data-testid="edit-category">
                <option value="">— None —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
            <div>
              <div className="font-semibold text-sm">Active</div>
              <div className="text-xs text-gray-500">Visible to customers in storefront</div>
            </div>
            <button type="button" onClick={() => update("is_active", !form.is_active)}
              data-testid="edit-toggle-active"
              className={`w-12 h-7 rounded-full transition relative ${form.is_active ? "bg-[#15803D]" : "bg-gray-300"}`}>
              <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all ${form.is_active ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-full bg-gray-100 font-bold hover:bg-gray-200"
              data-testid="edit-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="qd-btn-green flex-1 disabled:opacity-60"
              data-testid="edit-save">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
